<template>
  <div class="page-block">
    <div class="assets-page-head">
      <h2 class="assets-title">资产</h2>
      <div v-if="activeTab === 'image'" class="assets-head-upload">
        <input
          ref="uploader"
          class="uploader-input"
          type="file"
          accept="image/*"
          @change="onUploadChange"
        />
        <div class="upload-toolbar-anchor">
          <button type="button" class="toolbar-upload-btn" @click.stop="toggleUploadTypeMenu">+ 上传素材</button>
          <div v-show="showUploadTypeMenu" class="upload-type-dropdown" @click.stop>
            <button
              v-for="opt in uploadTypeMenuOptions"
              :key="opt.value"
              type="button"
              class="upload-type-item"
              @click="chooseUploadType(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="tab-row">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        class="tab-btn"
        :class="{ active: activeTab === tab.value }"
        @click="onMainTabChange(tab.value)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="activeTab === 'image'" class="image-filter-row">
      <button
        v-for="f in imageFilters"
        :key="f.value"
        type="button"
        class="filter-btn"
        :class="{ active: imageFilter === f.value }"
        @click="imageFilter = f.value"
      >
        {{ f.label }}
      </button>
    </div>

    <div
      v-if="showUploadTypeMenu && activeTab === 'image'"
      class="upload-menu-backdrop"
      @click="showUploadTypeMenu = false"
    ></div>

    <div class="asset-grid assets-grid-list">
      <div
        v-for="item in displayList"
        :key="item.id"
        class="asset-card"
        role="button"
        tabindex="0"
        @click="openPreview(item)"
        @keydown.enter="openPreview(item)"
      >
        <button type="button" class="asset-delete-btn" title="删除" @click.stop="deleteAsset(item)">×</button>
        <span class="asset-type-badge">{{ assetTypeLabel(item.assetType) }}</span>
        <div class="asset-thumb-frame">
          <template v-if="activeTab === 'video'">
            <div v-if="isPlaceholderAsset(item.cover)" class="asset-thumb-inner placeholder-block"></div>
            <video
              v-else
              class="asset-thumb-media asset-video asset-video-thumb"
              :src="mediaUrl(getVideoSrc(item))"
              muted
              playsinline
              preload="metadata"
            ></video>
          </template>
          <template v-else>
            <div v-if="isPlaceholderAsset(item.cover)" class="asset-thumb-inner placeholder-block"></div>
            <img
              v-else
              class="asset-thumb-media asset-thumb-img"
              :src="mediaUrl(item.cover)"
              :alt="item.name"
            />
          </template>
        </div>
        <div class="asset-meta">
          <span>{{ item.name }}</span>
          <small v-if="item.updateTime">{{ formatTime(item.updateTime) }}</small>
        </div>
      </div>
    </div>

    <div v-if="previewVisible" class="mask" @click.self="closePreview">
      <div class="asset-preview-modal">
        <button type="button" class="modal-close" @click="closePreview">×</button>
        <h3 class="preview-title">{{ previewIsVideo ? "视频预览" : "图片预览" }}</h3>
        <div class="preview-body">
          <div class="preview-media">
            <div class="preview-media-inner">
              <template v-if="previewIsVideo">
                <div v-if="isPlaceholderAsset(previewItem.cover)" class="preview-placeholder placeholder-block"></div>
                <video v-else class="preview-video" :src="mediaUrl(getVideoSrc(previewItem))" controls playsinline></video>
              </template>
              <template v-else>
                <div v-if="isPlaceholderAsset(previewItem.cover)" class="preview-placeholder placeholder-block"></div>
                <img v-else class="preview-img" :src="mediaUrl(previewItem.cover)" :alt="previewItem.name" />
              </template>
            </div>
          </div>

          <div class="preview-side">
            <template v-if="previewIsVideo">
              <div class="video-info-panel">
                <div class="info-row"><span class="info-k">名称</span><span class="info-v">{{ previewItem.name }}</span></div>
                <div class="info-row" v-if="previewItem.duration">
                  <span class="info-k">时长</span><span class="info-v">{{ previewItem.duration }}</span>
                </div>
                <div class="info-row">
                  <span class="info-k">类型</span><span class="info-v">{{ assetTypeLabel(previewItem.assetType) }}</span>
                </div>
                <div class="info-row" v-if="previewItem.fileSize">
                  <span class="info-k">大小</span><span class="info-v">{{ formatFileSize(previewItem.fileSize) }}</span>
                </div>
                <div class="info-row" v-if="previewItem.updateTime">
                  <span class="info-k">更新</span><span class="info-v">{{ formatTime(previewItem.updateTime) }}</span>
                </div>
              </div>
            </template>

            <template v-else>
              <div v-if="editForm.assetType === 'role'" class="role-detail-form">
                <div class="detail-field">
                  <label>角色名</label>
                  <input v-model="editForm.name" maxlength="20" />
                </div>
                <div class="detail-field">
                  <label>年龄</label>
                  <input v-model="editForm.age" maxlength="20" />
                </div>
                <div class="detail-field">
                  <label>性别</label>
                  <input v-model="editForm.gender" maxlength="20" />
                </div>
                <div class="detail-field">
                  <label>描述</label>
                  <textarea v-model="editForm.description" rows="10" />
                </div>
                <div class="detail-field type-switch">
                  <label>类型</label>
                  <select v-model="editForm.assetType" class="type-select">
                    <option value="role">角色</option>
                    <option value="scene">场景</option>
                    <option value="material">素材</option>
                  </select>
                </div>
                <div class="detail-actions">
                  <button type="button" class="secondary-btn" @click="closePreview">取消</button>
                  <button type="button" class="main-btn" @click="saveImageAsset">保存</button>
                </div>
              </div>

              <div v-else-if="editForm.assetType === 'scene'" class="role-detail-form">
                <div class="detail-field">
                  <label>场景名</label>
                  <input v-model="editForm.name" maxlength="20" />
                </div>
                <div class="detail-field">
                  <label>摘要</label>
                  <input v-model="editForm.summary" maxlength="100" />
                </div>
                <div class="detail-field">
                  <label>描述</label>
                  <textarea v-model="editForm.description" rows="10" />
                </div>
                <div class="detail-field type-switch">
                  <label>类型</label>
                  <select v-model="editForm.assetType" class="type-select">
                    <option value="role">角色</option>
                    <option value="scene">场景</option>
                    <option value="material">素材</option>
                  </select>
                </div>
                <div class="detail-actions">
                  <button type="button" class="secondary-btn" @click="closePreview">取消</button>
                  <button type="button" class="main-btn" @click="saveImageAsset">保存</button>
                </div>
              </div>

              <div v-else class="role-detail-form">
                <div class="detail-field">
                  <label>资产名</label>
                  <input v-model="editForm.name" maxlength="20" />
                </div>
                <div class="detail-field">
                  <label>摘要</label>
                  <input v-model="editForm.summary" maxlength="100" />
                </div>
                <div class="detail-field">
                  <label>描述</label>
                  <textarea v-model="editForm.description" rows="10" />
                </div>
                <div class="detail-field type-switch">
                  <label>类型</label>
                  <select v-model="editForm.assetType" class="type-select">
                    <option value="role">角色</option>
                    <option value="scene">场景</option>
                    <option value="material">素材</option>
                  </select>
                </div>
                <div class="detail-actions">
                  <button type="button" class="secondary-btn" @click="closePreview">取消</button>
                  <button type="button" class="main-btn" @click="saveImageAsset">保存</button>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div v-if="previewIsVideo" class="preview-footer-actions">
          <button type="button" class="main-btn" @click="closePreview">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { userApi } from "../api/user";

export default {
  name: "AssetsPage",
  data() {
    return {
      activeTab: "image",
      imageFilter: "all",
      uploadImageAssetType: "material",
      showUploadTypeMenu: false,
      uploadTypeMenuOptions: [
        { label: "上传角色", value: "role" },
        { label: "上传场景", value: "scene" },
        { label: "上传素材", value: "material" }
      ],
      tabs: [
        { label: "图片", value: "image" },
        { label: "视频", value: "video" }
      ],
      imageFilters: [
        { label: "全部", value: "all" },
        { label: "角色", value: "role" },
        { label: "场景", value: "scene" },
        { label: "素材", value: "material" }
      ],
      assets: { image: [], video: [] },
      previewVisible: false,
      previewItem: null,
      editForm: {
        id: 0,
        name: "",
        assetType: "material",
        age: "",
        gender: "",
        summary: "",
        description: ""
      }
    };
  },
  computed: {
    displayList() {
      const list = this.assets[this.activeTab] || [];
      if (this.activeTab !== "image" || this.imageFilter === "all") {
        return list;
      }
      return list.filter(i => (i.assetType || "") === this.imageFilter);
    },
    previewIsVideo() {
      return this.previewItem && this.previewItem.assetType === "video";
    }
  },
  async created() {
    this.refreshAssets();
  },
  methods: {
    onMainTabChange(val) {
      this.activeTab = val;
      this.showUploadTypeMenu = false;
      if (val === "video") {
        this.imageFilter = "all";
      }
    },
    assetTypeLabel(type) {
      const m = { role: "角色", scene: "场景", material: "素材", video: "视频" };
      return m[type] || type || "—";
    },
    mediaUrl(src) {
      const s = String(src || "");
      if (!s) return "";
      if (/^https?:\/\//i.test(s)) return s;
      return s.startsWith("/") ? s : `/${s}`;
    },
    formatFileSize(bytes) {
      const n = Number(bytes) || 0;
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatTime(ts) {
      const t = Number(ts);
      if (!t) return "—";
      const d = new Date(t * 1000);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(
        d.getHours()
      ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    hydrateEditForm(item) {
      this.editForm = {
        id: item.id,
        name: item.name || "",
        assetType: item.assetType && item.assetType !== "video" ? item.assetType : "material",
        age: item.age || "",
        gender: item.gender || "",
        summary: item.summary || "",
        description: item.description || ""
      };
    },
    openPreview(item) {
      if (!item) return;
      this.showUploadTypeMenu = false;
      this.previewItem = { ...item };
      if (item.assetType !== "video") {
        this.hydrateEditForm(item);
      }
      this.previewVisible = true;
    },
    closePreview() {
      this.previewVisible = false;
      this.previewItem = null;
    },
    async saveImageAsset() {
      try {
        await userApi.updateAsset({
          id: this.editForm.id,
          name: this.editForm.name,
          assetType: this.editForm.assetType,
          age: this.editForm.age,
          gender: this.editForm.gender,
          summary: this.editForm.summary,
          description: this.editForm.description
        });
        await this.refreshAssets();
        this.closePreview();
      } catch (err) {
        window.alert((err && err.message) || "保存失败");
      }
    },
    async refreshAssets() {
      try {
        const data = await userApi.assets();
        if (data && data.image && data.video) {
          this.assets = data;
        }
      } catch (e) {
        // keep empty state as fallback
      }
    },
    toggleUploadTypeMenu() {
      const token = localStorage.getItem("user_token");
      if (!token) {
        this.$root.$emit("open-login");
        return;
      }
      this.showUploadTypeMenu = !this.showUploadTypeMenu;
    },
    chooseUploadType(assetType) {
      this.uploadImageAssetType = assetType;
      this.showUploadTypeMenu = false;
      this.$nextTick(() => {
        const el = this.$refs.uploader;
        if (el) el.click();
      });
    },
    async onUploadChange(e) {
      const file = e && e.target && e.target.files && e.target.files[0];
      if (!file) return;
      e.target.value = "";

      try {
        const assetType = this.activeTab === "video" ? "video" : this.uploadImageAssetType;
        await userApi.uploadAsset({ file, assetType, name: file.name });
        await this.refreshAssets();
      } catch (err) {
        window.alert((err && err.message) || "上传失败");
      }
    },
    isPlaceholderAsset(src) {
      return /dummyimage\.com/i.test(String(src || ""));
    },
    getVideoSrc(item) {
      return (item && (item.url || item.src || item.cover)) || "";
    },
    deleteAsset(item) {
      if (!item) return;
      if (!window.confirm(`确认删除「${item.name || "图片"}」吗？`)) return;
      userApi
        .deleteAsset(item.id)
        .then(() => userApi.assets())
        .then(data => {
          if (data && data.image && data.video) {
            this.assets = data;
          }
        })
        .catch(err => {
          window.alert((err && err.message) || "删除失败");
        });
    }
  }
};
</script>

<style scoped>
.assets-page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  position: relative;
  z-index: 1600;
}

.assets-title {
  margin: 0;
  font-size: 24px;
}

.assets-head-upload {
  display: flex;
  position: absolute;
  right: 0px;
  top: 0px;
}

.upload-toolbar-anchor {
  position: relative;
}

.upload-type-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 160px;
  padding: 6px 0;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #2a2a2e;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  z-index: 1601;
}

.upload-type-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: #e8e8e8;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.upload-type-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.upload-menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: transparent;
}

.toolbar-upload-btn {
  padding: 8px 16px;
  border-radius: 10px;
  border: 1px dashed rgba(26, 188, 120, 0.85);
  background: #19b06b;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.toolbar-upload-btn:hover {
  background: rgba(26, 188, 120, 0.2);
  color: #fff;
}

.assets-grid-list {
  grid-template-columns: repeat(auto-fill, minmax(228px, 1fr));
  margin-top:12px;
}

.asset-card {
  position: relative;
  cursor: pointer;
  padding: 0;
}

.uploader-input {
  display: none;
}

/* 列表缩略统一视口：宽 200 × 高 400，内容 contain 居中不变形 */
.asset-thumb-frame {
  width: 300px;
  max-width: 100%;
  height: 120px;
  margin: 0 auto;
  border-radius: 10px;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.asset-meta {
  font-size: 12px;
  padding: 0 10px 10px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.asset-meta span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.asset-meta small {
  flex-shrink: 0;
  white-space: nowrap;
  color: #9a9a9a;
}

.asset-thumb-media,
.asset-thumb-inner {
  display: block;
}

.asset-thumb-img,
.asset-thumb-media.asset-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.asset-thumb-inner.placeholder-block {
  width: 100%;
  height: 100%;
  aspect-ratio: unset;
}

.tab-row{
  margin-bottom:12px;
}

.image-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-btn {
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.25);
  color: #ccc;
  cursor: pointer;
  font-size: 13px;
}

.filter-btn.active {
  border-color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b;
  color: #fff;
}

.asset-type-badge {
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 2;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  background: rgb(25 176 107);
  
  color: #fff;
  pointer-events: none;
}

.asset-video {
  display: block;
  background: #111;
}

/* 列表缩略：不展示控件、不抢点击，仅弹窗内可播放 */
.asset-video-thumb {
  pointer-events: none;
}

.asset-delete-btn {
  position: absolute;
  right: 10px;
  top: 6px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #19b06b;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  cursor: pointer;
  line-height: 1;
  font-size: 26px;
  padding: 0;
  z-index: 3;
}

.asset-delete-btn:hover {
  background: rgba(120, 255, 134, 0.75);
}

.placeholder-block {
  background: #444444;
  aspect-ratio: 14 / 9;
}

/* 覆盖全局 .asset-card img { width:100% }，避免列表缩略被拉变形 */
.asset-card .asset-thumb-frame img.asset-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 0;
}

/* 预览弹窗（高于上传类型遮罩） */
.mask {
  position: fixed;
  inset: 0;
  z-index: 2500;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.asset-preview-modal {
  position: relative;
  width: min(920px, 100%);
  max-height: 90vh;
  overflow: auto;
  background: #1a1a1e;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 20px 22px 16px;
  color: #e8e8e8;
}

.modal-close {
  position: absolute;
  right: 14px;
  top: 10px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.preview-title {
  margin: 0 0 16px;
  font-size: 17px;
  font-weight: 600;
  padding-right: 40px;
}

.preview-body {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: flex-start;
}

.preview-media {
  flex: 1 1 650px;
  min-width: min(650px, 100%);
}

.preview-media-inner {
  width: 100%;
  min-height:500px;
  max-height: min(520px, 65vh);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
  border-radius: 10px;
  overflow: hidden;
}

.preview-img,
.preview-video {
  display: block;
  max-width: 100%;
  max-height: min(520px, 65vh);
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  vertical-align: middle;
}

.preview-placeholder {
  width: 100%;
  min-height: min(520px, 65vh);
  border-radius: 10px;
}

.preview-media-inner .preview-placeholder.placeholder-block {
  aspect-ratio: unset;
}

.preview-side {
  flex: 1 1 200px;
  min-width: 200px;
}

.video-info-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 14px;
}

.info-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.info-k {
  flex: 0 0 48px;
  color: #888;
}

.info-v {
  color: #e0e0e0;
  word-break: break-all;
}

.role-detail-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-field label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: #aaa;
}

.detail-field input,
.detail-field textarea,
.type-select {
  width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.35);
  color: #eee;
  padding: 8px 10px;
  font-size: 14px;
}

.detail-field textarea {
  resize: vertical;
  min-height: 120px;
}

.type-switch {
  margin-top: 4px;
}

.detail-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.secondary-btn {
  padding: 8px 18px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #ddd;
  cursor: pointer;
}

.main-btn {
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  background: #19b06b;
  color: #fff;
  cursor: pointer;
}

.preview-footer-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style>
